import { useInfiniteQuery } from '@tanstack/react-query';
import { apiCall } from '../../lib/api.js';
import { useAccessToken, requireToken } from '../../lib/use-access-token.js';

export interface AuditEvent {
  id: string;
  occurredAt: string;
  companyId: string | null;
  actorType: 'user' | 'system' | 'integration';
  actorId: string | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  outcome: 'success' | 'failure' | 'denied';
  reason: string | null;
  requestId: string | null;
  ip: string | null;
  userAgent?: string | null;
}

export interface AuditFilters {
  companyId?: string;
  actorId?: string;
  action?: string;
  outcome?: 'success' | 'failure' | 'denied';
  targetType?: string;
  targetId?: string;
  from?: string;
  to?: string;
  limit?: number;
}

export function useAuditEvents(filters: AuditFilters) {
  const { token } = useAccessToken();
  return useInfiniteQuery({
    queryKey: ['audit', filters, token] as const,
    enabled: !!token,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({
      pageParam,
      signal,
    }: {
      pageParam: string | undefined;
      signal?: AbortSignal;
    }): Promise<{ items: AuditEvent[]; nextCursor: string | null }> => {
      const url = new URL('/v1/audit-events', window.location.origin);
      if (filters.companyId) url.searchParams.set('companyId', filters.companyId);
      if (filters.actorId) url.searchParams.set('actorId', filters.actorId);
      if (filters.action) url.searchParams.set('action', filters.action);
      if (filters.outcome) url.searchParams.set('outcome', filters.outcome);
      if (filters.targetType) url.searchParams.set('targetType', filters.targetType);
      if (filters.targetId) url.searchParams.set('targetId', filters.targetId);
      if (filters.from) url.searchParams.set('from', filters.from);
      if (filters.to) url.searchParams.set('to', filters.to);
      url.searchParams.set('limit', String(filters.limit ?? 50));
      if (pageParam) url.searchParams.set('cursor', pageParam);
      return apiCall(`${url.pathname}?${url.searchParams.toString()}`, {
        token: requireToken(token),
        ...(signal ? { signal } : {}),
      });
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}
